import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../app/hooks';
import {
  selectFeatureFlagHitl3,
  selectFeatureFlagHitlExpressTimer,
  selectHitlExpressTimerMaxSeconds,
  selectIsHitl7Enabled,
} from '../redux/features/config/config.selector';
import {
  selectIsExpressTimerElapsed,
  selectTaskAssignedSessionStartedAt,
} from '../redux/features/taskRouter/taskRouter.selector';
import {
  selectIsCarExited,
  selectSessionId,
} from '../redux/features/sessionBoundary/sessionBoundary.selectors';
import { setExpressTimerElapsed } from '../redux/features/taskRouter/taskRouter.slice';

/**
 * HITL 3.0 — headless express-mode latch.
 *
 * Extracted from the header <Timer/>, which hitl_3_0 removes from the header now
 * that the countdown lives in the Pulse "Menu Board Time" tile. Besides showing
 * the time, that Timer was the source of truth that latched
 * `isExpressTimerElapsed` once the order crossed `max_seconds` — including for
 * sessions that never received a task_assigned `session_started_at` (where
 * `useExpressTimer` cannot anchor and so never latches on its own). This hook
 * preserves exactly that behaviour without rendering anything.
 *
 * Self-gates on hitl_3_0 + hitl_express_timer, so it is inert in the legacy UX
 * (where the header still renders the <Timer/>).
 *
 * HITL-1662 puts the countdown BACK in the header under hitl_7_0, which makes
 * the <Timer/> the source of truth again — and it carries this same latch
 * effect internally. So this hook also self-gates OFF under hitl_7_0: exactly
 * one of the two latches runs at a time, never both. Without that, two
 * independent one-second intervals would count the same session in parallel
 * and race to dispatch the same flag.
 *
 *   flags                 who counts + latches
 *   ────────────────────  ─────────────────────────
 *   legacy                header <Timer/>
 *   hitl_3_0 only         this hook (no Timer rendered)
 *   hitl_3_0 + hitl_7_0   header <Timer/> again
 */
const useExpressTimerLatch = () => {
  const dispatch = useAppDispatch();
  const sessionId = useAppSelector(selectSessionId);
  const isHitl3 = useAppSelector(selectFeatureFlagHitl3);
  const isHitl7 = useAppSelector(selectIsHitl7Enabled);
  const isExpressTimerEnabled = useAppSelector(
    selectFeatureFlagHitlExpressTimer
  );
  const maxSeconds = useAppSelector(selectHitlExpressTimerMaxSeconds);
  const sessionStartedAt = useAppSelector(selectTaskAssignedSessionStartedAt);
  const alreadyElapsed = useAppSelector(selectIsExpressTimerElapsed);
  const carExited = useAppSelector(selectIsCarExited);
  const [duration, setDuration] = useState(0);

  // !isHitl7 — the header <Timer/> owns the latch again under 7.0 (see above).
  const expressActive = isHitl3 && !isHitl7 && isExpressTimerEnabled;

  // Count elapsed seconds from the task_assigned session_started_at when present,
  // otherwise from mount (the fallback the header Timer provided).
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (sessionId) {
      const parsedStart = expressActive
        ? new Date(sessionStartedAt).getTime()
        : NaN;
      const startTime =
        !isNaN(parsedStart) && sessionStartedAt
          ? parsedStart
          : new Date().getTime();
      const getDuration = () =>
        setDuration(
          Math.max(0, Math.floor((new Date().getTime() - startTime) / 1000))
        );
      getDuration();
      timer = setInterval(getDuration, 1000);
    }
    return () => {
      if (timer) {
        clearInterval(timer);
        setDuration(0);
      }
    };
  }, [sessionId, expressActive, sessionStartedAt]);

  const over = expressActive && maxSeconds > 0 && duration > maxSeconds;

  // Latch express mode once over the limit. Do not re-latch after car exit — the
  // stale session_started_at would fight useExpressTimer's car-exit reset.
  useEffect(() => {
    if (over && !alreadyElapsed && !carExited) {
      dispatch(setExpressTimerElapsed(true));
    }
  }, [over, alreadyElapsed, carExited, dispatch]);
};

export default useExpressTimerLatch;
